/**
 * Composer.ErrorBanner — inline error feedback strip.
 *
 * Renders below the textarea (above the action bar) when the composer
 * surfaces a recoverable error: failed attachment upload, network drop,
 * BE rate-limit, etc. Self-dismisses via the X — recoverable, non-blocking.
 *
 * v1 ref: errors were surfaced via the global Sonner toast in
 * `QueryInput.tsx`. v2 keeps the message *adjacent* to the input that
 * produced it (closer feedback loop, Nielsen heuristic #1: visibility of
 * system status). Toasts are reserved for cross-surface notifications.
 */

import type * as React from "react";
import { AlertTriangle, X } from "lucide-react";
import { cn } from "../../lib/cn";

export interface ComposerErrorBannerProps {
  /** Localised error copy. `null`/empty → renders nothing. */
  message: string | null | undefined;
  /** Optional dismiss handler — when set, an X button appears. */
  onDismiss?: () => void;
  className?: string;
}

export const ComposerErrorBanner: React.FC<ComposerErrorBannerProps> = ({
  message,
  onDismiss,
  className,
}) => {
  if (!message) return null;
  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn(
        "mx-2.5 my-1 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/[0.06] px-2.5 py-1.5",
        "animate-chip-in",
        className,
      )}
    >
      <AlertTriangle
        size={13}
        strokeWidth={2}
        className="mt-px shrink-0 text-destructive"
        aria-hidden="true"
      />
      <span className="flex-1 text-[11px] text-destructive leading-snug">
        {message}
      </span>
      {onDismiss ? (
        <button
          type="button"
          aria-label="Chiudi avviso"
          onClick={onDismiss}
          className="-mr-1 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-destructive/70 transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-destructive/40"
        >
          <X size={9} strokeWidth={2.5} />
        </button>
      ) : null}
    </div>
  );
};
ComposerErrorBanner.displayName = "Composer.ErrorBanner";
